// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { spacing, typography } from "@recidiviz/design-system";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { OpportunityType } from "~datatypes";
import { palette } from "~design-system";
import { withPresenterManager } from "~hydration-utils";

import {
  useFeatureVariants,
  useRootStore,
} from "../../components/StoreProvider";
import { JusticeInvolvedPerson } from "../../WorkflowsStore";
import { WorkflowsFormLayoutPresenter } from "../../WorkflowsStore/presenters/WorkflowsFormLayoutPresenter";
import cssVars from "../CoreConstants.module.scss";
import { usePersonTracking } from "../hooks/usePersonTracking";
import ModelHydrator from "../ModelHydrator";
import { NavigationBackButton } from "../NavigationBackButton";
import {
  NAV_BAR_HEIGHT,
  NavigationLayout,
  SCROLL_PADDING,
} from "../NavigationLayout";
import { OpportunityPreviewPanel } from "../OpportunityCaseloadView/OpportunityPreviewPanel";
import { OpportunityFormProvider } from "../Paperwork/OpportunityFormContext";
import { FormUsIaEarlyDischargeParole } from "../Paperwork/US_IA/EarlyDischarge/FormUsIaEarlyDischargeParole";
import { FormUsIaEarlyDischargeProbation } from "../Paperwork/US_IA/EarlyDischarge/FormUsIaEarlyDischargeProbation";
import { FormEarnedDischarge } from "../Paperwork/US_ID/EarnedDischarge/FormEarnedDischarge";
import { FormUsMiClassification } from "../Paperwork/US_MI/MiClassification/FormUsMiClassification";
import { FormUsMiSCCReviewV2 } from "../Paperwork/US_MI/SCCReviewV2/FormUsMiSCCReviewV2";
import { FormUsMoWorkRelease } from "../Paperwork/US_MO/WorkRelease/FormUsMoWorkRelease";
import { FormUsNcCreditReductionReview } from "../Paperwork/US_NC/CreditReductionReview/FormUsNcCreditReductionReview";
import { FormUsPaAdminSupervision } from "../Paperwork/US_PA/AdminSupervision/FormUsPaAdminSupervision";
import { WorkflowsFormUsTnSuspensionOfDirectSupervision } from "../Paperwork/US_TN/SuspensionOfDirectSupervision/WorkflowsFormUsTnSuspensionOfDirectSupervision";
import { FormUsTnDiagnosticClassification2026 } from "../Paperwork/US_TN/UsTnDiagnosticClassification2026/FormUsTnDiagnosticClassification2026";
import { FormUsTnReclassification2026 } from "../Paperwork/US_TN/UsTnReclassification2026/FormUsTnReclassification2026";
import { FormUsTnReclassification2026V2 } from "../Paperwork/US_TN/UsTnReclassification2026V2/FormUsTnReclassification2026V2";
import { navigateBackFromOpportunityForm } from "../utils/workflowsUtils";
import WorkflowsCompliantReportingForm from "../WorkflowsCompliantReportingForm/WorkflowsCompliantReportingForm";
import WorkflowsEarlyTerminationDeferredForm from "../WorkflowsEarlyTerminationDeferredForm/WorkflowsEarlyTerminationDeferredForm";
import WorkflowsEarlyTerminationForm from "../WorkflowsEarlyTerminationForm/WorkflowsEarlyTerminationForm";
import { useOpportunitySidePanel } from "../WorkflowsJusticeInvolvedPersonProfile/OpportunitySidePanelContext";
import WorkflowsLSUForm from "../WorkflowsLSUForm";
import WorkflowsUsAzReleaseToTransitionProgramForm from "../WorkflowsUsAzReleaseToTransitionProgramForm";
import WorkflowsUsCaSupervisionLevelDowngradeForm from "../WorkflowsUsCaSupervisionLevelDowngradeForm";
import WorkflowsUsCaSupervisionLevelDowngradeForm3043 from "../WorkflowsUsCaSupervisionLevelDowngradeForm3043";
import WorkflowsUsNeSupervisionDowngradeForm from "../WorkflowsUsNeSupervisionDowngradeForm";
import WorkflowsUsTnExpirationForm from "../WorkflowsUsTnExpirationForm";
import WorkflowsUsTnReclassForm from "../WorkflowsUsTnReclassForm";
import WorkflowsUsTxAnnualReportStatusForm from "../WorkflowsUsTxAnnualReportStatusForm";
import WorkflowsUsTxEarlyReleaseFromSupervisionForm from "../WorkflowsUsTxEarlyReleaseFromSupervisionForm";

export const FORM_SIDEBAR_WIDTH = 400;

const Wrapper = styled.div`
  ${typography.Sans16}
  align-items: stretch;
  display: grid;
  grid-template-columns: ${rem(FORM_SIDEBAR_WIDTH)} 1fr;
  position: relative;
  min-height: 100vh;
  width: 100%;

  @media screen and (min-width: ${cssVars.breakpointSm}) {
    padding-right: 0;
  }
`;

const Sidebar = styled.div`
  background: ${palette.marble1};
`;

const SidebarSection = styled.section<{ $extraScrollBuffer?: boolean }>`
  position: sticky;
  inset: 0;
  max-height: ${`calc(100vh - ${rem(NAV_BAR_HEIGHT)})`};
  overflow-y: auto;

  padding: ${rem(spacing.lg)} ${rem(spacing.md)};

  ${({ $extraScrollBuffer }) =>
    $extraScrollBuffer &&
    `padding-bottom: ${rem(spacing.lg + SCROLL_PADDING)};`}

  &:first-child {
    border-bottom: 1px solid ${palette.slate20};
  }
`;

const BackButtonWrapper = styled.div`
  padding-bottom: ${rem(spacing.lg)};
`;

const FormWrapper = styled.div``;

// TODO(#6017): In order for the FormLayout to integrate with insights and the
// supervisor homepage, we'll need to update the form components themselves to
// take a JusticeInvolvedPerson parameter. This has already been done for US_ID forms.
const FormComponents = {
  WorkflowsCompliantReportingForm,
  WorkflowsEarlyTerminationForm,
  WorkflowsEarlyTerminationDeferredForm,
  FormEarnedDischarge,
  WorkflowsLSUForm,
  WorkflowsUsTnExpirationForm,
  WorkflowsUsTnReclassForm,
  FormUsTnDiagnosticClassification2026,
  FormUsTnReclassification2026,
  FormUsTnReclassification2026V2,
  WorkflowsUsCaSupervisionLevelDowngradeForm,
  WorkflowsUsCaSupervisionLevelDowngradeForm3043,
  FormUsPaAdminSupervision,
  FormUsIaEarlyDischargeParole,
  FormUsIaEarlyDischargeProbation,
  FormUsMiSCCReviewV2,
  WorkflowsUsAzReleaseToTransitionProgramForm,
  WorkflowsFormUsTnSuspensionOfDirectSupervision,
  WorkflowsUsNeSupervisionDowngradeForm,
  WorkflowsUsTxAnnualReportStatusForm,
  WorkflowsUsTxEarlyReleaseFromSupervisionForm,
  FormUsMoWorkRelease,
  FormUsNcCreditReductionReview,
  FormUsMiClassification,
};

export type OpportunityFormComponentName = keyof typeof FormComponents;

type WorkflowsFormLayoutProps = {
  selectedPerson: JusticeInvolvedPerson;
  selectedOpportunityType: OpportunityType;
};

/**
 * A wrapper for the FormLayout that's used from workflows views - access to
 * state fields in the store is consolidated here and passed into the layout component.
 */
export const WorkflowsFormLayoutWrapper = observer(
  function WorkflowsFormLayoutWrapper() {
    const {
      workflowsStore: { selectedPerson, selectedOpportunityType },
    } = useRootStore();
    if (!selectedPerson || !selectedOpportunityType) return null;

    return (
      <WorkflowsFormLayout
        selectedPerson={selectedPerson}
        selectedOpportunityType={selectedOpportunityType}
      />
    );
  },
);

/**
 * Interactive form layout UI for a given person and opportunity; this UI is shared
 * between workflows and the supervisor homepage.
 *
 * ***Note*** Because of the shared nature of this layout, it's best to keep necessary
 * data or state as parameters that are passed in, rather than pulling stateful info
 * from a store within the component itself.
 */
const HydratedWorkflowsFormLayout = observer(
  function HydratedWorkflowsFormLayout({
    presenter,
  }: {
    presenter: WorkflowsFormLayoutPresenter;
  }) {
    const { currentView, setCurrentView } = useOpportunitySidePanel();
    const { classificationScrollBuffer } = useFeatureVariants();
    const navigate = useNavigate();
    const { pathname, key: locationKey } = useLocation();
    const { officerPseudoId, supervisorPseudoId } = useParams();

    const { selectedOpportunity, selectedPerson, workflowsMethodologyUrl } =
      presenter;

    usePersonTracking(selectedPerson, () => {
      selectedOpportunity?.form?.trackViewed();
    });

    const formContents = selectedOpportunity?.form?.formContents;

    const FormComponent = formContents && FormComponents[formContents];

    const handleBack = () => {
      if (currentView === "OPPORTUNITY_PREVIEW") {
        navigateBackFromOpportunityForm({
          navigate,
          pathname,
          urlSection: selectedOpportunity?.config.urlSection,
          officerPseudoId,
          supervisorPseudoId,
          locationKey,
        });
      } else {
        setCurrentView("OPPORTUNITY_PREVIEW");
      }
    };

    return (
      <Wrapper>
        <Sidebar>
          <NavigationLayout
            externalMethodologyUrl={workflowsMethodologyUrl}
            isFixed={false}
          />
          <SidebarSection $extraScrollBuffer={!!classificationScrollBuffer}>
            <BackButtonWrapper>
              <NavigationBackButton action={{ onClick: handleBack }}>
                Back
              </NavigationBackButton>
            </BackButtonWrapper>
            <OpportunityPreviewPanel
              opportunity={selectedOpportunity}
              selectedPerson={selectedPerson}
              isFormView
            />
          </SidebarSection>
        </Sidebar>

        <FormWrapper>
          {FormComponent && (
            <OpportunityFormProvider value={selectedOpportunity.form}>
              <FormComponent opportunity={selectedOpportunity} />
            </OpportunityFormProvider>
          )}
        </FormWrapper>
      </Wrapper>
    );
  },
);

function usePresenter({
  selectedPerson,
  selectedOpportunityType,
}: WorkflowsFormLayoutProps) {
  const { workflowsStore, firestoreStore, tenantStore } = useRootStore();

  if (!selectedPerson || !selectedOpportunityType) {
    return null;
  }

  return new WorkflowsFormLayoutPresenter(
    selectedPerson,
    selectedOpportunityType,
    workflowsStore,
    firestoreStore,
    tenantStore,
  );
}

export const WorkflowsFormLayout = withPresenterManager({
  usePresenter,
  ManagedComponent: HydratedWorkflowsFormLayout,
  managerIsObserver: true,
  HydratorComponent: ModelHydrator,
});
